import { computed, type ComputedRef, type Ref, ref } from 'vue'

import { useThrottleFn } from '@vueuse/core'
import { type AxiosError, isAxiosError } from 'axios'
import { useRouter } from 'vue-router'
import { toast } from 'vue-sonner'

import { i18n } from '@/i18n'
import { routeNames } from '@/router/routeNames'

type UseErrorComposableType<T> = {
  handleError: (error: AxiosError | Error | unknown) => void
  errors: Ref<ErrorElementsType<T>>
  addErrorObj: (error: Partial<Record<keyof T, string>>) => void
  hasErrors: ComputedRef<boolean>
  clearErrors: () => void
}

export type ErrorElementsType<T> = {
  [K in keyof T]?: string
} & {
  [key: string]: string
}

export interface ErrorResponse {
  message: string
  key?: string
  errors?: Record<string, string[]>
}

interface PreviousError {
  error: AxiosError | null
  stamp: Date
}

const previousError = ref<PreviousError>({
  error: null,
  stamp: new Date(),
})

const throttleInvalidError = useThrottleFn(() => {
  const errorMsg = i18n.global.t('common.error')
  const description = i18n.global.t('common.invalidOrExpiredToken')
  console.error('Token invalid')
  toast.error(errorMsg, {
    description: description,
  })
}, 2000)

const throttleDefaultError = useThrottleFn(() => {
  const errorMsg = i18n.global.t('common.error')
  const description = i18n.global.t('common.unknownError')
  console.error('An error occurred')
  toast.error(errorMsg, {
    description: description,
  })
}, 1000)

export const useError = <T>(): UseErrorComposableType<T> => {
  const { t, te } = i18n.global
  const router = useRouter()

  const errors = ref<ErrorElementsType<T>>({} as ErrorElementsType<T>) as Ref<
    ErrorElementsType<T>
  >

  const parseErrors = (
    err: AxiosError<ErrorResponse>
  ): ErrorElementsType<T> => {
    if (!err.response?.data?.errors) return {} as ErrorElementsType<T>
    const errors = Object.entries(err.response.data.errors)

    const transformErrors = {} as ErrorElementsType<T>
    errors.forEach(([key, value]) => {
      let newVal = ''
      if (Array.isArray(value)) {
        // newVal = value.join(',')
        newVal = String(value[0])
      }
      ;(transformErrors as Record<string, string>)[key] = newVal
    })
    return transformErrors
  }

  const clearErrors = (): void => {
    errors.value = {} as ErrorElementsType<T>
  }

  const hasErrors = computed(() => {
    if (!errors.value) return false

    const items = Object.keys(errors.value)
    for (const item of items) {
      const errorStringExists = Boolean(errors.value[item])
      if (errorStringExists) {
        return true
      }
    }
    return false
  })

  const addErrorObj = (error: Partial<ErrorElementsType<T>>): void => {
    const errorObj = {} as ErrorElementsType<T>
    Object.keys(error).forEach(key => {
      ;(errorObj as Record<string, string>)[key] = error[key] as string
    })
    errors.value = errorObj
  }

  const isSameAsPreviousError = (error: AxiosError): boolean => {
    const isSameAsPreviousError =
      error.code === previousError.value?.error?.code
    const sameTimestamp =
      new Date().getTime() - previousError.value.stamp.getTime() < 3000

    return isSameAsPreviousError && sameTimestamp
  }

  const showTranslatedErrorMessage = (error: AxiosError): boolean => {
    const curError = error.response?.data as ErrorResponse | undefined

    if (!curError) {
      return false
    }

    if (curError.key) {
      const errorText = `common.errors.${curError.key}`

      if (te(errorText)) {
        console.error(errorText)
        toast.error(t('common.error'), {
          description: t(errorText),
        })
        return true
      }
    }

    if (curError.message) {
      console.error(curError.message)
      toast.error(t('common.error'), {
        description: curError.message,
      })
      return true
    }

    return false
  }

  const handleTokenError = (error: AxiosError<any, any>): boolean => {
    const tokenErrors = [
      'Token invalid',
      'Token expired',
      'Not authorized',
      'Unauthorized',
    ]

    if (tokenErrors.includes(error.response?.data?.error as string)) {
      throttleInvalidError().then(() => {
        setTimeout(
          async () => await router?.push({ name: routeNames.Login }),
          1500
        )
      })
      return true
    }

    return false
  }

  const handleError = (error: AxiosError | Error | unknown): void => {
    if (!isAxiosError(error)) {
      console.error(error)
      throttleDefaultError()
      return
    }

    errors.value = parseErrors(error)

    if (isSameAsPreviousError(error)) {
      return
    }

    previousError.value = {
      error: error,
      stamp: new Date(),
    }

    const isTokenError = handleTokenError(error)
    if (isTokenError) return

    const success = showTranslatedErrorMessage(error)
    if (success) return

    throttleDefaultError()
  }
  return {
    addErrorObj,
    handleError,
    clearErrors,
    hasErrors,
    errors,
  }
}
